<template>
  <InputSelect
    name="Cerca impianto"
    :modelValue="modelValue"
    @update:modelValue="value => $emit('update:modelValue', value)"
    :options="searchOptions"
    :loading="isLoading"
    :disabled="disabled"
    id="_id"
    track-by="_id"
    :custom-label="implantSearchLabel"
    @select="event => $emit('select', event)"
    @remove="event => $emit('remove', event)"
    @search-change="onSearchChange"
  >
    <template #option="props">
      <div class="implant-search">
        <div class="implant-search-row">
          <div class="implant-search-col">
            <div class="implant-search-label">Occupante</div>
            <div class="implant-search-value">{{ displayClient(props.option.occupant) }}</div>
          </div>
          <div class="implant-search-col">
            <div class="implant-search-label">Proprietario</div>
            <div class="implant-search-value">{{ displayClient(props.option.client) }}</div>
          </div>
          <div v-if="props.option.archived" class="tag is-warning is-light is-small">Archiviato</div>
        </div>
        <div class="implant-search-row">
          <div class="implant-search-label">Luogo</div>
          <div class="implant-search-value">{{ displayPlace(props.option.place_occupant) }}</div>
        </div>
        <div class="implant-search-row">
          <div class="implant-search-label">Impianto</div>
          <div class="implant-search-value">
            <div class="tags are-small">
              <span class="tag is-light" v-for="(line, index) in formatImplantLines(props.option)" :key="index">{{ line }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #singleLabel="props">
      <div class="implant-search">
        <div class="implant-search-row">
          <div class="implant-search-col">
            <div class="implant-search-label">Occupante</div>
            <div class="implant-search-value">{{ displayClient(props.option.occupant) }}</div>
          </div>
          <div class="implant-search-col">
            <div class="implant-search-label">Proprietario</div>
            <div class="implant-search-value">{{ displayClient(props.option.client) }}</div>
          </div>
          <div v-if="props.option.archived" class="tag is-warning is-light is-small">Archiviato</div>
        </div>
        <div class="implant-search-row">
          <div class="implant-search-label">Luogo</div>
          <div class="implant-search-value">{{ displayPlace(props.option.place_occupant) }}</div>
        </div>
        <div class="implant-search-row">
          <div class="implant-search-label">Impianto</div>
          <div class="implant-search-value">
            <div class="tags are-small">
              <span class="tag is-light" v-for="(line, index) in formatImplantLines(props.option)" :key="index">{{ line }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </InputSelect>
</template>

<script>
export default {
  props: {
    modelValue: {
      type: Object,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    limit: {
      type: Number,
      default: 20
    },
    minLength: {
      type: Number,
      default: 2
    },
    debounceMs: {
      type: Number,
      default: 300
    }
  },
  emits: ['update:modelValue', 'select', 'remove', 'search-change'],
  data() {
    return {
      searchOptions: [],
      isLoading: false,
      debouncedSearch: null
    }
  },
  methods: {
    onSearchChange(text) {
      this.$emit('search-change', text)

      if (this.disabled) {
        this.searchOptions = []
        return
      }

      if (!this.debouncedSearch) {
        return
      }

      this.debouncedSearch(text)
    },
    fetchImplants(text) {
      const cleanText = (text || '').trim()
      if (!cleanText || cleanText.length < this.minLength) {
        this.searchOptions = []
        return
      }

      this.isLoading = true

      this.$http.get('/implant/search', { params: { q: cleanText, limit: this.limit } })
      .then(response => {
        this.searchOptions = response?.data || []
        this.isLoading = false
      })
      .catch(error => {
        console.log(error)
        alert('Errore')
        this.isLoading = false
      })
    },
    implantSearchLabel(implant) {
      if (!implant) {
        return ''
      }

      const occupant = this.displayClient(implant.occupant)
      const client = this.displayClient(implant.client)
      const place = this.displayPlace(implant.place_occupant)
      const implantName = this.$implantName(implant)

      return [occupant, client, place, implantName].filter(Boolean).join(' ')
    },
    displayClient(client) {
      return this.$clientName(client) || 'N/D'
    },
    displayPlace(place) {
      return this.$placeName(place) || 'N/D'
    },
    formatImplantLines(implant) {
      const raw = this.$implantName(implant)
      if (!raw) {
        return ['N/D']
      }

      const lines = raw.split('\n').map(line => line.trim()).filter(Boolean)
      return lines.length ? lines : ['N/D']
    }
  },
  mounted() {
    this.debouncedSearch = this.$debounce(this.fetchImplants.bind(this), this.debounceMs)
  },
  watch: {
    disabled(value) {
      if (value) {
        this.searchOptions = []
        this.isLoading = false
      }
    }
  }
}
</script>

<style scoped>
.implant-search {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.implant-search-row {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.implant-search-col {
  min-width: 140px;
}

.implant-search-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #7a7a7a;
}

.implant-search-value {
  font-weight: 600;
}
</style>

<style scoped>
:deep(.multiselect__option--highlight) {
  background: #d7e6ff;
  color: inherit;
}

:deep(.multiselect__option--highlight::after) {
  background: #d7e6ff;
  color: inherit;
}
</style>
